

import React, { useState } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewLoyaltyRuleModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        is_active: true,
        earn_basis: 'OrderTotal',
        earn_rate: '1.00',
        earn_rounding: 'Nearest',
        minimum_order_status: 'Completed',
        hold_days: 7,
        expiry_policy: 'FixedMonths',
        expiry_months: 24,
        redemption_conversion: '100.00',
        min_redeem_points: 100,
        redeem_increment: 50,
        rule_version: '1.0'
    });

    const [formErrors, setFormErrors] = useState({});
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Name is required';
        if (!formData.earn_basis) errors.earn_basis = 'Earn Basis is required';
        if (!formData.earn_rate) errors.earn_rate = 'Earn Rate is required';
        if (!formData.redemption_conversion) errors.redemption_conversion = 'Redemption Conversion is required';
        if (!formData.rule_version) errors.rule_version = 'Rule Version is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                name: formData.name,
                is_active: formData.is_active,
                earn_basis: formData.earn_basis,
                earn_rate: parseFloat(formData.earn_rate),
                earn_rounding: formData.earn_rounding || null,
                minimum_order_status: formData.minimum_order_status || null,
                hold_days: formData.hold_days ? parseInt(formData.hold_days) : null,
                expiry_policy: formData.expiry_policy || null,
                expiry_months: formData.expiry_months ? parseInt(formData.expiry_months) : null,
                redemption_conversion: parseFloat(formData.redemption_conversion),
                min_redeem_points: formData.min_redeem_points ? parseInt(formData.min_redeem_points) : null,
                redeem_increment: formData.redeem_increment ? parseInt(formData.redeem_increment) : null,
                rule_version: formData.rule_version
            };

            await onSave(payload);
            setFormData({
                name: '',
                is_active: true,
                earn_basis: 'OrderTotal',
                earn_rate: '1.00',
                earn_rounding: 'Nearest',
                minimum_order_status: 'Completed',
                hold_days: 7,
                expiry_policy: 'FixedMonths',
                expiry_months: 24,
                redemption_conversion: '100.00',
                min_redeem_points: 100,
                redeem_increment: 50,
                rule_version: '1.0'
            });
            onClose();
        } catch (error) {
            console.error('Error saving Loyalty Rule:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Loyalty Rule',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '700px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Loyalty Rule</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MDBox mt={1}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={formData.is_active}
                                                    onChange={handleInputChange}
                                                    name="is_active"
                                                />
                                            }
                                            label="Active"
                                        />
                                    </MDBox>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Earn Basis</InputLabel>
                                        <Select
                                            name="earn_basis"
                                            value={formData.earn_basis}
                                            onChange={handleInputChange}
                                            label="Earn Basis"

                                        >
                                            <MenuItem value="OrderTotal">Order Total</MenuItem>
                                            <MenuItem value="TripDays">Trip Days</MenuItem>
                                            <MenuItem value="TripCategory">Trip Category</MenuItem>
                                            <MenuItem value="Fixed">Fixed</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Earn Rate"
                                        name="earn_rate"
                                        type="number"
                                        value={formData.earn_rate}
                                        onChange={handleInputChange}
                                        error={!!formErrors.earn_rate}
                                        helperText={formErrors.earn_rate}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Earn Rounding</InputLabel>
                                        <Select
                                            name="earn_rounding"
                                            value={formData.earn_rounding}
                                            onChange={handleInputChange}
                                            label="Earn Rounding"

                                        >
                                            <MenuItem value="Floor">Floor</MenuItem>
                                            <MenuItem value="Nearest">Nearest</MenuItem>
                                            <MenuItem value="Ceil">Ceil</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Minimum Order Status</InputLabel>
                                        <Select
                                            name="minimum_order_status"
                                            value={formData.minimum_order_status}
                                            onChange={handleInputChange}
                                            label="Minimum Order Status"

                                        >
                                            <MenuItem value="Completed">Completed</MenuItem>
                                            <MenuItem value="Ticketed">Ticketed</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Hold Days"
                                        name="hold_days"
                                        type="number"
                                        value={formData.hold_days}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Expiry Policy</InputLabel>
                                        <Select
                                            name="expiry_policy"
                                            value={formData.expiry_policy}
                                            onChange={handleInputChange}
                                            label="Expiry Policy"

                                        >
                                            <MenuItem value="None">None</MenuItem>
                                            <MenuItem value="FixedMonths">Fixed Months</MenuItem>
                                            <MenuItem value="RollingMonths">Rolling Months</MenuItem>
                                            <MenuItem value="EndOfYear">End Of Year</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Expiry Months"
                                        name="expiry_months"
                                        type="number"
                                        value={formData.expiry_months}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Redemption Conversion"
                                        name="redemption_conversion"
                                        type="number"
                                        value={formData.redemption_conversion}
                                        onChange={handleInputChange}
                                        error={!!formErrors.redemption_conversion}
                                        helperText={formErrors.redemption_conversion}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Minimum Redeem Points"
                                        name="min_redeem_points"
                                        type="number"
                                        value={formData.min_redeem_points}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Redeem Increment"
                                        name="redeem_increment"
                                        type="number"
                                        value={formData.redeem_increment}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Rule Version"
                                        name="rule_version"
                                        value={formData.rule_version}
                                        onChange={handleInputChange}
                                        error={!!formErrors.rule_version}
                                        helperText={formErrors.rule_version}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Loyalty Rule'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewLoyaltyRuleModal;

